import React from "react";
import { Mail, Calendar, FileText, Settings } from "lucide-react";
import { DD_MM_YYYY } from "src/helpers";
import { POAdditionalInfo, PurchaseOrder, UpdatePurchaseOrderOpsNoteParams } from "src/types";
import { useAuthorisation } from "src/context";
import { EditOpsNote } from "src/components";
import { CollapsibleSection } from "src/components/ui/collapsible";

interface PurchaseOrderOperationsSectionProps {
  senderEmail?: string;
  sentAt?: string;
  operationsNote?: POAdditionalInfo["operationsNote"];
  onUpdateOpsNote?: (params: UpdatePurchaseOrderOpsNoteParams) => void;
  purchaseOrderId: string;
  initiallyOpen?: boolean;
  withCollapsible?: boolean;
}

const PurchaseOrderOperationsSection: React.FC<PurchaseOrderOperationsSectionProps> = ({
  senderEmail,
  sentAt,
  operationsNote,
  onUpdateOpsNote,
  purchaseOrderId,
  initiallyOpen = true,
  withCollapsible = true
}) => {
  const { userRole } = useAuthorisation();

  if (!senderEmail && !operationsNote) {
    return null;
  }

  const content = (
    <div className="p-6 space-y-4">
      {userRole !== "VENDOR" ? (
        <>
          {/* Sender & Date Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {senderEmail && (
              <div className="bg-secondary-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Mail className="w-4 h-4 text-primary-600" />
                  <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">Sender Email</span>
                </div>
                <p className="text-sm font-medium text-secondary-900">{senderEmail}</p>
              </div>
            )}
            {sentAt && (
              <div className="bg-secondary-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Calendar className="w-4 h-4 text-primary-600" />
                  <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">Sent At</span>
                </div>
                <p className="text-sm font-medium text-secondary-900">{DD_MM_YYYY.formatDate(new Date(sentAt))}</p>
              </div>
            )}
          </div>

          {/* Operations Note */}
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <FileText className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-secondary-800 mb-2">Operations Note</p>
                <EditOpsNote<PurchaseOrder>
                  id={purchaseOrderId}
                  timestamp={undefined}
                  opsNote={operationsNote}
                  onUpdateOpsNote={onUpdateOpsNote}
                  isTextButton={true}
                  textTruncateLength={1200}
                />
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Vendor View */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {senderEmail && (
              <div className="bg-secondary-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Mail className="w-4 h-4 text-primary-600" />
                  <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">
                    Healf Operations
                  </span>
                </div>
                <p className="text-sm font-medium text-secondary-900">{senderEmail}</p>
              </div>
            )}
            {sentAt && (
              <div className="bg-secondary-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Calendar className="w-4 h-4 text-primary-600" />
                  <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">Sent At</span>
                </div>
                <p className="text-sm font-medium text-secondary-900">{DD_MM_YYYY.formatDate(new Date(sentAt))}</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  if (!withCollapsible) {
    return (
      <section className="rounded-2xl border border-secondary-200 bg-white shadow-soft">
        <div className="flex items-center gap-2 border-b border-secondary-100 px-6 py-4">
          <Settings className="h-5 w-5 text-primary-600" />
          <h3 className="text-base font-semibold text-secondary-900">Operations</h3>
        </div>
        {content}
      </section>
    );
  }

  return (
    <div className="bg-white border border-secondary-200 rounded-xl shadow-soft overflow-hidden">
      <CollapsibleSection
        initiallyOpen={initiallyOpen}
        title={
          <h3 className="text-lg font-semibold text-secondary-800 flex items-center">
            <Settings className="w-5 h-5 mr-2 text-primary-600" />
            Operations
          </h3>
        }
      >
        {content}
      </CollapsibleSection>
    </div>
  );
};

export { PurchaseOrderOperationsSection };
